import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff } from 'lucide-react'
import { CoworkLogo } from '@/components/ui/CoworkLogo'
import { AuthBrandPanel } from '@/components/auth/AuthBrandPanel'
import { GoogleIcon, FacebookIcon } from '@/components/ui/SocialIcons'
import { login, resendVerification, EmailNotVerifiedError } from '@/services/authService'
import { useAuthStore } from '@/store/authStore'
import { getProfile } from '@/services/profileService'
import { needsOnboarding } from '@/lib/onboarding'

const inputClass =
  'mt-1.5 flex h-12 w-full rounded-lg border border-[#E7E5FF] bg-white px-4 text-sm text-text shadow-sm transition-colors placeholder:text-text-muted/70 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/15'

const outlineButtonClass =
  'flex h-12 flex-1 items-center justify-center gap-2 rounded-lg border border-[#E7E5FF] bg-white px-4 text-sm font-medium text-text transition-colors hover:bg-[#F7F6FF]'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const redirectTo = (location.state as { from?: Location })?.from?.pathname || '/'
  const setAuth = useAuthStore((s) => s.setAuth)
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  // ล็อกอินไม่ผ่านเพราะยังไม่ยืนยันอีเมล — แสดงปุ่มส่งลิงก์ยืนยันอีกครั้ง
  const [needsVerify, setNeedsVerify] = useState(false)
  const [resendMsg, setResendMsg] = useState('')
  const [resending, setResending] = useState(false)

  const handleResend = async () => {
    setResendMsg('')
    setResending(true)
    try {
      setResendMsg(await resendVerification(email))
    } catch (err) {
      setResendMsg(err instanceof Error ? err.message : 'ส่งอีเมลไม่สำเร็จ')
    } finally {
      setResending(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setNeedsVerify(false)
    setResendMsg('')
    setLoading(true)
    try {
      const { access_token, refresh_token } = await login(email, password)
      setAuth(access_token, refresh_token)
      useAuthStore.getState().markLogin()   // ล็อกอินใหม่ = ป๊อปอัปหน้าแรกขึ้นใหม่ได้อีกครั้ง
      try {
        const profile = await getProfile()
        if (needsOnboarding(profile)) {
          navigate('/onboarding', { replace: true, state: { from: redirectTo } })
          return
        }
      } catch { /* โหลดโปรไฟล์ไม่สำเร็จ — ไม่บล็อกการ login ปกติ */ }
      navigate(redirectTo, { replace: true })
    } catch (err) {
      if (err instanceof EmailNotVerifiedError) setNeedsVerify(true)
      setError(err instanceof Error ? err.message : 'เข้าสู่ระบบไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen">
      <AuthBrandPanel />

      {/* Right — login form (45%) */}
      <div className="flex w-full flex-col items-center justify-center bg-white px-4 py-10 lg:w-[45%]">
        {/* Compact logo shown only when the branding panel is hidden */}
        <div className="mb-8 lg:hidden">
          <CoworkLogo />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="w-full max-w-[440px]"
        >
          <div className="text-left">
            <h2 className="text-2xl font-semibold text-[#19172A]">เข้าสู่ระบบบัญชีของคุณ</h2>
            <p className="mt-2 text-sm text-text-muted">
              ยังไม่มีบัญชี?{' '}
              <Link to="/register" className="font-medium text-accent hover:underline">
                สมัครสมาชิก
              </Link>
            </p>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#" className={outlineButtonClass}>
              <GoogleIcon className="size-4" aria-hidden />
              <span className="text-sm font-medium">Google</span>
            </a>
            <a href="#" className={outlineButtonClass}>
              <FacebookIcon className="size-5 text-[#1877F2]" aria-hidden />
              <span className="text-sm font-medium">Facebook</span>
            </a>
          </div>

          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="h-px w-full bg-[#E7E5FF]" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-text-muted">หรือ</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email-login" className="text-sm font-medium text-text">
                อีเมล
              </label>
              <input
                type="email"
                id="email-login"
                autoComplete="email"
                placeholder="you@example.com"
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password-login" className="text-sm font-medium text-text">
                  รหัสผ่าน
                </label>
                <Link to="#" className="text-xs font-medium text-accent hover:underline">
                  ลืมรหัสผ่าน?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password-login"
                  autoComplete="current-password"
                  placeholder="********"
                  className={`${inputClass} pr-11`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                  aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                >
                  {showPassword ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-rose-500">{error}</p>}
            {needsVerify && (
              <div className="rounded-lg border border-[#E7E5FF] bg-[#F7F6FF] p-3 text-sm text-text-muted">
                <p>{resendMsg || 'ยังไม่ได้รับอีเมลยืนยัน? เราส่งลิงก์ใหม่ให้ได้'}</p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="mt-2 font-medium text-accent hover:underline disabled:opacity-50"
                >
                  {resending ? 'กำลังส่ง...' : 'ส่งอีเมลยืนยันอีกครั้ง'}
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 flex h-12 w-full items-center justify-center rounded-lg bg-gradient-to-r from-accent to-accent2 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:shadow-accent/40 disabled:pointer-events-none disabled:opacity-50"
            >
              {loading ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
